<template>
    <div>
        <div class="d-secret-input">
            <DInput
                v-bind="$attrs"
                :model-value="modelValue"
                :placeholder="isMasked ? MASK : placeholder"
                :disabled="disabled"
                type="password"
                autocomplete="new-password"
                @update:modelValue="updateValue"
            />
            <DButton
                v-if="canClear"
                icon="close"
                :title="__('messages.secret_input.clear')"
                @click="emit('update:cleared', true)"
            />
            <DButton
                v-if="cleared && !disabled"
                icon="undo-variant"
                :title="__('messages.secret_input.undo')"
                @click="emit('update:cleared', false)"
            />
        </div>
        <small
            v-if="cleared"
            class="text-warning"
        >
            {{ __('messages.secret_input.will_be_removed') }}
        </small>
    </div>
</template>

<script setup>
// Input for an encrypted credential that is never sent to the frontend. Shows a mask when a value is
// stored, and lets the user keep it (leave empty), replace it (type a new one) or remove it (clear).
// Backend counterpart: App\Traits\Secrets\ValidatesSecretFields; bind `v-model:cleared` to `clear_<field>`.
import { computed } from 'vue'

defineOptions({ inheritAttrs: false })

const MASK = '********'

const props = defineProps({
    modelValue: {
        type: String,
        default: null
    },
    isStored: {
        type: Boolean,
        default: false
    },
    cleared: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'update:cleared'])

const isMasked = computed(() => props.isStored && !props.cleared)
const canClear = computed(() => isMasked.value && !props.modelValue && !props.disabled)

const updateValue = (value) => {
    emit('update:modelValue', value === '' ? null : value)

    if (value && props.cleared) {
        emit('update:cleared', false)
    }
}
</script>

<style scoped lang="scss">
.d-secret-input {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}
</style>
